import React, { useState } from "react";
import styled from "styled-components";
import HighlightText from "./HighlightText";
import ImageLightbox from "./ImageLightbox";
import { getThumbnailUrl } from "../../util/imageUtils";

/**
 * Shared components and utilities for displaying menu items with sections.
 * Used by RestaurantDetails and RestaurantResult pages.
 */

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Helper to extract section name (handles both subdocument and legacy string format)
 */
const getSectionName = (section) => {
  if (!section) return null;
  if (typeof section === 'string') return section; // Legacy format
  return section.name || null; // Subdocument format
};

const getSectionOrder = (section) => {
  if (!section) return null;
  if (typeof section === 'string') return null; // Legacy format has no order
  return section.order;
};

/**
 * Group menu items by their section name.
 * Items without a section are grouped under null key.
 * Section is a subdocument: {name: string, order: number} or null
 * Also tracks the minimum section.order for each section for sorting.
 */
export const groupItemsBySection = (items) => {
  const grouped = {};
  const sectionOrderMap = {}; // Track min section.order per section name
  items.forEach((item) => {
    const sectionName = getSectionName(item.section);
    const sectionOrder = getSectionOrder(item.section);
    if (!grouped[sectionName]) {
      grouped[sectionName] = [];
      sectionOrderMap[sectionName] = sectionOrder ?? Infinity;
    } else if (sectionOrder != null && sectionOrder < sectionOrderMap[sectionName]) {
      sectionOrderMap[sectionName] = sectionOrder;
    }
    grouped[sectionName].push(item);
  });
  return { grouped, sectionOrderMap };
};

/**
 * Sort section keys by section order (original menu order).
 * Falls back to alphabetical if no order, null/empty sections last.
 */
export const sortSectionKeys = (keys, sectionOrderMap = {}) => {
  return keys.sort((a, b) => {
    // Null/empty sections always go last
    const aIsNull = a === null || a === "null" || a === "";
    const bIsNull = b === null || b === "null" || b === "";
    if (aIsNull && bIsNull) return 0;
    if (aIsNull) return 1;
    if (bIsNull) return -1;

    // Sort by section order if available
    const orderA = sectionOrderMap[a] ?? Infinity;
    const orderB = sectionOrderMap[b] ?? Infinity;
    if (orderA !== orderB) return orderA - orderB;

    // Fall back to alphabetical
    return a.localeCompare(b);
  });
};

/**
 * Get display name for a section (handles null/empty as "Other Items")
 */
export const getSectionDisplayName = (section) => {
  if (!section || section === "null") return "Other Items";
  return section;
};

/**
 * Get vegan menu items for public display.
 * Uses veganOptions which is the canonical source for confirmed vegan items.
 * veganOptions now includes section data (added to schema).
 */
export const getDisplayableMenuItems = (restaurant) => {
  // Use veganOptions - the canonical source for public vegan display
  // Section field is now part of veganOptions schema
  if (restaurant.veganOptions && restaurant.veganOptions.length > 0) {
    return restaurant.veganOptions;
  }

  return [];
};

// =============================================================================
// STYLED COMPONENTS - Section Headers
// =============================================================================

export const SectionHeader = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-primary, #2e7d32);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--color-primary, #2e7d32);

  &:first-child {
    margin-top: 0;
  }
`;

export const SectionHeaderCompact = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--color-text-secondary, #666666);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0.25rem;
`;

// =============================================================================
// STYLED COMPONENTS - Menu Item Cards (Detail Page)
// =============================================================================

export const MenuItemCard = styled.div`
  padding: 0.75rem 0 0.75rem 1rem;
  border-left: 3px solid var(--color-primary, #2e7d32);
  margin-bottom: 0.75rem;
  background: var(--color-gray-light, #f5f5f5);
  border-radius: 0 4px 4px 0;
  transition: background-color 0.15s ease;

  &:hover {
    background: #eeeeee;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const MenuItemName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: var(--color-text-primary, #212121);
  margin-bottom: 0.25rem;
`;

export const MenuItemDescription = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-secondary, #666666);
  line-height: 1.4;
`;

// =============================================================================
// STYLED COMPONENTS - Dish Image Thumbnails
// =============================================================================

const MenuItemCardInner = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

const DishThumbnail = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }

  @media (min-width: 600px) {
    width: 160px;
    height: 160px;
  }
`;

const MenuItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

// =============================================================================
// STYLED COMPONENTS - Carousel Items
// =============================================================================

export const CarouselItemContainer = styled.div`
  padding: 0.5rem 0;
`;

export const CarouselSectionLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 500;
  color: var(--color-text-secondary, #666666);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0.15rem;
`;

export const CarouselItemName = styled.div`
  font-weight: 600;
  color: var(--color-text-primary, #212121);
`;

export const CarouselItemDescription = styled.div`
  font-size: 0.9em;
  color: var(--color-text-secondary, #666666);
  margin-top: 0.1rem;
`;

// =============================================================================
// STYLED COMPONENTS - Container for menu sections
// =============================================================================

export const MenuContainer = styled.div`
  margin-top: 1.5rem;
`;

export const MenuTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary, #212121);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e0e0e0;
`;

// =============================================================================
// COMPONENTS
// =============================================================================

/**
 * Renders a single menu item as a card (for detail page).
 * Shows dish thumbnail when imageUrl is available, with tap-to-expand lightbox.
 */
export const MenuItemCardComponent = ({ item, searchQuery }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const thumbnailUrl = getThumbnailUrl(item.imageUrl);

  return (
    <>
      <MenuItemCard>
        <MenuItemCardInner>
          {thumbnailUrl && (
            <DishThumbnail
              src={thumbnailUrl}
              alt={item.option}
              onClick={() => setLightboxOpen(true)}
              loading="lazy"
            />
          )}
          <MenuItemText>
            <MenuItemName>
              <HighlightText text={item.option} query={searchQuery} />
            </MenuItemName>
            {item.description && (
              <MenuItemDescription>
                <HighlightText text={item.description} query={searchQuery} />
              </MenuItemDescription>
            )}
          </MenuItemText>
        </MenuItemCardInner>
      </MenuItemCard>
      {lightboxOpen && (
        <ImageLightbox
          imageUrl={item.imageUrl}
          name={item.option}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
};

/**
 * Renders a single menu item for carousel
 * Section is a subdocument: {name: string, order: number} or null
 */
export const CarouselMenuItem = ({ item, showSection = true, searchQuery }) => {
  const sectionName = getSectionName(item.section);
  return (
    <CarouselItemContainer>
      {showSection && sectionName && (
        <CarouselSectionLabel>{sectionName}</CarouselSectionLabel>
      )}
      <CarouselItemName>
        <HighlightText text={item.option} query={searchQuery} />
      </CarouselItemName>
      {item.description && (
        <CarouselItemDescription>
          <HighlightText text={item.description} query={searchQuery} />
        </CarouselItemDescription>
      )}
    </CarouselItemContainer>
  );
};

/**
 * Renders all menu items grouped by section (for detail page)
 */
export const GroupedMenuDisplay = ({ items, searchQuery }) => {
  if (!items || items.length === 0) {
    return null;
  }

  const { grouped, sectionOrderMap } = groupItemsBySection(items);
  const sectionKeys = sortSectionKeys(Object.keys(grouped), sectionOrderMap);

  return (
    <MenuContainer>
      <MenuTitle>Vegan Options</MenuTitle>
      {sectionKeys.map((section) => {
        const sectionItems = grouped[section];
        const displayName = getSectionDisplayName(section);
        const isOtherItems = !section || section === "null";

        return (
          <div key={section || "other"}>
            {/* Only show section header if there are multiple sections or it's a named section */}
            {(sectionKeys.length > 1 || !isOtherItems) && (
              <SectionHeader>{displayName}</SectionHeader>
            )}
            {sectionItems.map((item, index) => (
              <MenuItemCardComponent key={item._id || index} item={item} searchQuery={searchQuery} />
            ))}
          </div>
        );
      })}
    </MenuContainer>
  );
};

export default GroupedMenuDisplay;
